import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Bar, Doughnut, Line, defaults } from 'react-chartjs-2';
import { useSelector } from 'react-redux';

import DashboardBZonePerDayWidget from '@legacy-components/admin/dashboardWidgets/DashboardBZonePerDayWidget';

import { Button } from '@shared/ui/components/Button';
import { Icon } from '@shared/ui/components/Icon';
import { Portlet } from '@shared/ui/components/Portlet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/ui/components/Tabs';

import { COLORS } from '../../constants/colors';
import { selectEvent } from '../../redux/preferences/selectors';
import axios from '../../services/api';
import DashboardParkingCapacitiesWidget from './dashboardWidgets/DashboardParkingCapacitiesWidget';

defaults.global.defaultFontSize = 16;

const StatisticsDashboard = () => {
  const [data, setData] = useState(null);
  const event = useSelector(selectEvent);

  const getData = () => {
    axios.get(`/events/${event.id}/statistics`).then((response) => {
      setData(response.data);
    });
  };

  useEffect(() => {
    getData();
    const interval = setInterval(getData, 60 * 1000);
    return () => clearInterval(interval);
  }, [event.id]);

  if (!data) {
    return null;
  }

  const scansPerMinute = {
    labels: data.transitsPer30Minutes
      .map((_, index) =>
        moment()
          .add(1, 'day')
          .subtract(index * 30, 'minutes')
          .format('HH:ss')
      )
      .reverse(),
    datasets: [
      {
        label: 'Scans / 30 min',
        fill: false,
        lineTension: 0.1,
        backgroundColor: 'rgba(75,192,192,0.4)',
        borderColor: COLORS.success,
        borderCapStyle: 'butt',
        borderDash: [],
        borderDashOffset: 0.0,
        borderJoinStyle: 'miter',
        pointBorderColor: 'rgba(75,192,192,1)',
        pointBackgroundColor: '#fff',
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: 'rgba(75,192,192,1)',
        pointHoverBorderColor: 'rgba(220,220,220,1)',
        pointHoverBorderWidth: 2,
        pointRadius: 1,
        pointHitRadius: 10,
        minSize: 0,
        data: data.transitsPer30Minutes.reverse(),
      },
    ],
  };

  const wristbandsPerMinute = {
    labels: data.wristbandsPrintedPer30Minutes
      .map((_, index) =>
        moment()
          .add(1, 'day')
          .subtract(index * 30, 'minutes')
          .format('HH:ss')
      )
      .reverse(),
    datasets: [
      {
        label: 'Wristbands Printed / 30 min',
        fill: true,
        lineTension: 0.1,
        backgroundColor: COLORS.brand,
        borderColor: COLORS.brand,
        borderCapStyle: 'butt',
        borderDash: [],
        borderDashOffset: 0.0,
        borderJoinStyle: 'miter',
        pointBorderColor: COLORS.success,
        pointBackgroundColor: '#fff',
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointRadius: 0,
        pointHitRadius: 10,
        minSize: 0,
        data: data.wristbandsPrintedPer30Minutes.reverse(),
      },
    ],
  };

  const zonesAmount = {
    datasets: [
      {
        data: data.crewPerZone[0].map((item) => item.amount),
        backgroundColor: Object.values(COLORS),
      },
    ],
    labels: data.crewPerZone[0].map((item) => item.name),
  };

  const crewPerProfile = {
    labels: data.crewPerProfile.map((item) => item.name),
    datasets: [
      {
        label: 'Amount of crew expecting today',
        fill: true,
        lineTension: 0.1,
        backgroundColor: COLORS.warning,
        borderColor: COLORS.warning,
        borderCapStyle: 'butt',
        borderDash: [],
        borderDashOffset: 0.0,
        borderJoinStyle: 'miter',
        pointBorderColor: COLORS.success,
        pointBackgroundColor: '#fff',
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointRadius: 0,
        pointHitRadius: 10,
        minSize: 0,
        data: data.crewPerProfile.map((item) => item.amount),
      },
    ],
  };

  const cateringData = {};

  data.cateringForcedToday.forEach((option) => {
    if (!cateringData.hasOwnProperty(option.name)) {
      cateringData[option.name] = {
        forced: 0,
        claimed: 0,
        requested: 0,
      };
    }
    cateringData[option.name].forced = option.amount;
  });

  data.cateringRequestsToday.forEach((option) => {
    if (!cateringData.hasOwnProperty(option.name)) {
      cateringData[option.name] = {
        forced: 0,
        claimed: 0,
        requested: 0,
      };
    }
    cateringData[option.name].requested = option.amount;
  });

  data.cateringScansToday.forEach((option) => {
    if (!cateringData.hasOwnProperty(option.name)) {
      cateringData[option.name] = {
        forced: 0,
        claimed: 0,
        requested: 0,
      };
    }
    cateringData[option.name].claimed = option.amount;
  });

  const catering = {
    labels: Object.keys(cateringData),
    datasets: [
      {
        label: 'Requested',
        backgroundColor: COLORS.success,
        borderColor: COLORS.success,
        borderWidth: 1,
        data: Object.values(cateringData).map((data) => data.requested),
      },
      {
        label: 'Claimed',
        backgroundColor: COLORS.warning,
        borderColor: COLORS.warning,
        borderWidth: 1,
        data: Object.values(cateringData).map((data) => data.claimed),
      },
      {
        label: 'Forced',
        backgroundColor: COLORS.danger,
        borderColor: COLORS.danger,
        borderWidth: 1,
        data: Object.values(cateringData).map((data) => data.forced),
      },
    ],
  };

  const redeemedVouchers = {
    labels: data.unredeemedVouchers.map((v) => v.name),
    datasets: [
      {
        label: 'Redeemed',
        backgroundColor: COLORS.success,
        borderColor: COLORS.success,
        borderWidth: 1,
        data: data.unredeemedVouchers.map((v) => v.redeemed),
        stack: 1,
      },
      {
        label: 'Unredeemed',
        backgroundColor: COLORS.warning,
        borderColor: COLORS.warning,
        borderWidth: 1,
        data: data.unredeemedVouchers.map((v) => v.unredeemed),
        stack: 1,
      },
    ],
  };

  const zone1 = {
    datasets: [
      {
        data: [data.crewZone1Today - data.crewZone1, data.crewZone1],
        backgroundColor: [COLORS.metal, COLORS.primary],
      },
    ],
    labels: ['Still Expecting', 'In Zone 1'],
  };

  const enlarge = () => {
    const fontSizeIncrement = 2;

    defaults.global.defaultFontSize = defaults.global.defaultFontSize + fontSizeIncrement;

    let currentBodySize = document.body.style.fontSize ? parseInt(document.body.style.fontSize, 10) : 13;
    document.body.style.setProperty('font-size', `${currentBodySize + fontSizeIncrement}px`, 'important');
    document.documentElement.style.setProperty('font-size', `${currentBodySize + fontSizeIncrement}px`, 'important');

    // Force re-render
    setData(null);
    getData();
  };

  return (
    <div className="m-content" key="content">
      <Tabs defaultValue="wristbands">
        <div className="flex justify-between align-center">
          <TabsList>
            <TabsTrigger value="wristbands">Wristbands</TabsTrigger>
            <TabsTrigger value="scanning">Scanning</TabsTrigger>
            <TabsTrigger value="parking">Parking</TabsTrigger>
            <TabsTrigger value="catering">Catering</TabsTrigger>
          </TabsList>
          <Button onClick={enlarge} size="sm" className="float-right">
            <Icon icon="plus" />
            Groter!!!!
          </Button>
        </div>
        <TabsContent value="wristbands">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
            <Portlet>
              <Line data={wristbandsPerMinute} />
            </Portlet>
            <Portlet className="xl:row-start-2">
              <div className="m-widget1">
                <h3 className="m-widget1__title">Wristbands</h3>
                <div className="m-widget1__item">
                  <div className="row m-row--no-padding align-items-center">
                    <div className="col">
                      <span className="m-widget1__desc">Wristbands Printed Today</span>
                    </div>
                    <div className="col m--align-right">
                      <span className="m-widget1__number m--font-danger">{data.wristbandsPrintedToday}</span>
                    </div>
                  </div>
                </div>
                <div className="m-widget1__item">
                  <div className="row m-row--no-padding align-items-center">
                    <div className="col">
                      <span className="m-widget1__desc">Wristbands Printed Last 10 min</span>
                    </div>
                    <div className="col m--align-right">
                      <span className="m-widget1__number m--font-success">{data.wristbandsPrintedLast10Minutes}</span>
                    </div>
                  </div>
                </div>
                <div className="m-widget1__item">
                  <div className="row m-row--no-padding align-items-center">
                    <div className="col">
                      <span className="m-widget1__desc">Crew active today</span>
                    </div>
                    <div className="col m--align-right">
                      <span className="m-widget1__number m--font-brand">{data.crewToday}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Portlet>
            <Portlet className="md:col-span-2 md:row-span-2">
              <Bar
                plugins={[ChartDataLabels]}
                data={redeemedVouchers}
                options={{
                  scales: {
                    xAxes: [
                      {
                        stacked: false,
                        beginAtZero: true,
                        scaleLabel: {
                          labelString: 'Month',
                        },
                        ticks: {
                          stepSize: 1,
                          min: 0,
                          autoSkip: false,
                        },
                      },
                    ],
                  },
                }}
              />
            </Portlet>
          </div>
        </TabsContent>
        <TabsContent value="scanning">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
            <Portlet className="relative">
              <Doughnut
                plugins={[ChartDataLabels]}
                data={zone1}
                options={{
                  legend: {
                    position: 'right',
                  },
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 20,
                  right: 20,
                  zIndex: 3,
                  textAlign: 'right',
                }}
              >
                Total crew allowed in <br />
                Zone 1 today: <strong>{data.crewZone1Today}</strong>
              </div>
            </Portlet>
            <Portlet>
              <Line data={scansPerMinute} />
            </Portlet>
            <Portlet>
              <div className="m-widget1 padding">
                <h3 className="m-widget1__title">Scans</h3>
                <div className="m-widget1__item">
                  <div className="row m-row--no-padding align-items-center">
                    <div className="col">
                      <span className="m-widget1__desc">Crew scanned today at gates</span>
                    </div>
                    <div className="col m--align-right">
                      <span className="m-widget1__number m--font-brand">{data.crewScannedToday}</span>
                    </div>
                  </div>
                </div>
                <div className="m-widget1__item">
                  <div className="row m-row--no-padding align-items-center">
                    <div className="col">
                      <span className="m-widget1__desc">Scans last 10 minutes</span>
                    </div>
                    <div className="col m--align-right">
                      <span className="m-widget1__number m--font-danger">{data.transitsLast10Minutes}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Portlet>
            <Portlet>
              <Doughnut
                plugins={[ChartDataLabels]}
                data={zonesAmount}
                options={{
                  legend: {
                    position: 'right',
                  },
                }}
              />
            </Portlet>
            <Portlet className="md:col-span-2 md:row-span-2">
              <DashboardBZonePerDayWidget data={data.bZoneTransitsPerDay} />
            </Portlet>
            <Portlet>
              <div className="font-bold text-center">
                <div className="text-2xl text-gray-500 mb-8">Currently On Crew Camping: </div>
                <div className="text-4xl">{data.currentCrewCampingCount}</div>
              </div>
            </Portlet>
          </div>
        </TabsContent>
        <TabsContent value="parking">
          <DashboardParkingCapacitiesWidget data={data.parkingVouchers} />
        </TabsContent>
        <TabsContent value="catering">
          <Portlet>
            <Bar
              plugins={[ChartDataLabels]}
              data={catering}
              options={{
                scales: {
                  xAxes: [
                    {
                      stacked: false,
                      beginAtZero: true,
                      scaleLabel: {
                        labelString: 'Month',
                      },
                      ticks: {
                        stepSize: 1,
                        min: 0,
                        autoSkip: false,
                      },
                    },
                  ],
                },
              }}
            />
          </Portlet>
        </TabsContent>
      </Tabs>
    </div>
  );
};

StatisticsDashboard.propTypes = {
  event: PropTypes.object,
};

export default StatisticsDashboard;
